import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle, XCircle, Spinner, ArrowRight } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!orderId) { setStatus("failed"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${orderId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [orderId, clear]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-6 py-16">
      <div className="max-w-md w-full text-center fade-up">
        {status === "checking" && (
          <>
            <Spinner size={56} className="text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-head text-3xl mb-3">Confirming payment…</h1>
            <p className="text-muted-foreground">Please wait while we verify your transaction.</p>
          </>
        )}
        {status === "paid" && (
          <div className="border border-accent/30 bg-accent/5 p-10">
            <CheckCircle size={56} weight="duotone" className="text-accent mx-auto mb-6" />
            <h1 className="font-head text-3xl mb-3">Order Confirmed</h1>
            <p className="text-muted-foreground mb-8">Thank you. Your research order has been received and is now being processed.</p>
            <div className="flex flex-col gap-3">
              <Link to="/account" data-testid="view-orders-btn" className="flex items-center justify-center gap-2 bg-primary text-black label-mono py-4">View My Orders <ArrowRight size={16} weight="bold" /></Link>
              <Link to="/shop" className="border border-black/15 label-mono py-4 hover:border-primary hover:text-primary transition-colors">Continue Shopping</Link>
            </div>
          </div>
        )}
        {(status === "failed" || status === "timeout") && (
          <div className="border border-destructive/30 bg-destructive/5 p-10">
            <XCircle size={56} weight="duotone" className="text-destructive mx-auto mb-6" />
            <h1 className="font-head text-3xl mb-3">{status === "timeout" ? "Still Processing" : "Payment Issue"}</h1>
            <p className="text-muted-foreground mb-8">{status === "timeout" ? "Your payment is taking longer than expected. Check your order history shortly." : "We couldn't confirm your payment. Please try again."}</p>
            <Link to="/cart" className="inline-block bg-primary text-black label-mono py-4 px-8">Back to Cart</Link>
          </div>
        )}
      </div>
    </div>
  );
}
